package com.tovtalk.presentation.viewmodel.notifications

import com.tovtalk.domain.entity.Notification
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.repository.NotificationRepository
import com.tovtalk.presentation.state.PagingState
import com.tovtalk.presentation.state.UiState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

import com.tovtalk.presentation.ViewModel

/**
 * NotificationViewModel - unified feed (activity + admin notifications).
 * Supports mark as read (single / all).
 */
class NotificationViewModel(
    private val notificationRepository: NotificationRepository,
    private val scope: CoroutineScope
) : ViewModel() {

    // Unified feed (activity + admin)
    private val _unifiedNotificationsState = MutableStateFlow<PagingState<Notification>>(PagingState())
    val unifiedNotificationsState: StateFlow<PagingState<Notification>> = _unifiedNotificationsState.asStateFlow()

    private val _unreadCount = MutableStateFlow(0)
    val unreadCount: StateFlow<Int> = _unreadCount.asStateFlow()

    private val _markReadState = MutableStateFlow<UiState<Unit>>(UiState.Idle)
    val markReadState: StateFlow<UiState<Unit>> = _markReadState.asStateFlow()

    private var lastId: Int? = null
    private var lastCreatedAt: String? = null

    companion object {
        private const val PAGE_SIZE = 20
    }

    init {
        refreshUnreadCountInBackground()
    }

    /** Prefetch unread count by loading the first page of notifications (reuses the same API call). */
    private fun refreshUnreadCountInBackground() {
        scope.launch {
            when (val result = notificationRepository.getUnifiedNotifications(
                limit = PAGE_SIZE,
                lastId = null,
                lastCreatedAt = null
            )) {
                is DomainResult.Success -> {
                    val data = result.data
                    lastId = data.lastId
                    lastCreatedAt = data.lastCreatedAt
                    _unifiedNotificationsState.value = PagingState(
                        items = data.items,
                        hasMore = data.hasMore,
                        isLoading = false,
                        isLoadingMore = false
                    )
                    _unreadCount.value = data.items.count { !it.isRead }
                }
                else -> { /* keep 0 */ }
            }
        }
    }

    fun loadUnifiedNotifications(refresh: Boolean = false) {
        if (refresh) {
            lastId = null
            lastCreatedAt = null
            _unifiedNotificationsState.value = PagingState(isLoading = true)
        } else if (_unifiedNotificationsState.value.isLoading || !_unifiedNotificationsState.value.hasMore) {
            return
        } else {
            _unifiedNotificationsState.value = _unifiedNotificationsState.value.copy(isLoadingMore = true)
        }

        scope.launch {
            when (val result = notificationRepository.getUnifiedNotifications(
                limit = PAGE_SIZE,
                lastId = if (refresh) null else lastId,
                lastCreatedAt = if (refresh) null else lastCreatedAt
            )) {
                is DomainResult.Success -> {
                    val data = result.data
                    val newItems = data.items
                    val currentItems = if (refresh) emptyList() else _unifiedNotificationsState.value.items
                    lastId = data.lastId
                    lastCreatedAt = data.lastCreatedAt

                    _unifiedNotificationsState.value = PagingState(
                        items = currentItems + newItems,
                        hasMore = data.hasMore,
                        isLoading = false,
                        isLoadingMore = false
                    )
                    val allItems = currentItems + newItems
                    _unreadCount.value = allItems.count { !it.isRead }
                }
                is DomainResult.Error -> {
                    _unifiedNotificationsState.value = _unifiedNotificationsState.value.copy(
                        isLoading = false,
                        isLoadingMore = false,
                        hasMore = false,
                        error = result.message
                    )
                }
                is DomainResult.Loading -> {}
            }
        }
    }

    fun loadMoreNotifications() {
        loadUnifiedNotifications(refresh = false)
    }

    fun markAsRead(notification: Notification) {
        scope.launch {
            _markReadState.value = UiState.Loading
            val result = when (notification) {
                is Notification.Admin -> notificationRepository.markAdminNotificationAsRead(notification.id)
                is Notification.Activity -> notificationRepository.markNotificationAsRead(notification.id)
            }
            when (result) {
                is DomainResult.Success -> {
                    _markReadState.value = UiState.Success(Unit)
                    _unifiedNotificationsState.value = _unifiedNotificationsState.value.copy(
                        items = _unifiedNotificationsState.value.items.map { n ->
                            if (n.id == notification.id) when (n) {
                                is Notification.Admin -> n.copy(isRead = true)
                                is Notification.Activity -> n.copy(isRead = true)
                            } else n
                        }
                    )
                    _unreadCount.value = _unifiedNotificationsState.value.items.count { !it.isRead }
                }
                is DomainResult.Error -> _markReadState.value = UiState.Error(result.message)
                is DomainResult.Loading -> {}
            }
        }
    }

    fun deleteNotification(notificationId: Int) {
        scope.launch {
            when (val result = notificationRepository.deleteNotification(notificationId)) {
                is DomainResult.Success -> {
                    _unifiedNotificationsState.value = _unifiedNotificationsState.value.copy(
                        items = _unifiedNotificationsState.value.items.filter { it.id != notificationId }
                    )
                    _unreadCount.value = _unifiedNotificationsState.value.items.count { !it.isRead }
                }
                is DomainResult.Error -> {}
                is DomainResult.Loading -> {}
            }
        }
    }

    fun refresh() {
        loadUnifiedNotifications(refresh = true)
    }

    fun clearError() {
        _unifiedNotificationsState.value = _unifiedNotificationsState.value.copy(error = null)
    }

    fun markAllAsRead() {
        scope.launch {
            _markReadState.value = UiState.Loading
            when (val result = notificationRepository.markAllAsRead()) {
                is DomainResult.Success -> {
                    _unifiedNotificationsState.value = _unifiedNotificationsState.value.copy(
                        items = _unifiedNotificationsState.value.items.map { n ->
                            when (n) {
                                is Notification.Admin -> n.copy(isRead = true)
                                is Notification.Activity -> n.copy(isRead = true)
                            }
                        }
                    )
                    _unreadCount.value = 0
                    _markReadState.value = UiState.Success(Unit)
                }
                is DomainResult.Error -> _markReadState.value = UiState.Error(result.message)
                is DomainResult.Loading -> {}
            }
        }
    }
}
